import React, { Component } from 'react';
import './TabbedSidebar.css';

/**
 * TabbedSidebar
 *
 * Props:
 *   tabs       – array of { id, icon, label, content }
 *                  icon    – any renderable node (SVG, emoji, text)
 *                  label   – accessible aria-label for the tab button
 *                  content – renderable node shown in the sidebar panel
 *   children   – rendered in the main content area
 *   defaultTab – id of the initially active tab (defaults to first tab)
 *   contentRef – optional ref forwarded to the .sidebar-content div
 */
class TabbedSidebar extends Component {
    constructor(props) {
        super(props);
        const { tabs = [], defaultTab } = props;
        this.state = {
            activeTab: defaultTab || (tabs[0] && tabs[0].id),
            collapsed: false,
        };
    }

    selectTab = (id) => {
        this.setState((prev) => ({
            activeTab: id,
            // If the panel is collapsed and the user clicks a tab, expand it
            collapsed: prev.collapsed && prev.activeTab === id ? true : false,
        }));
    };

    toggleCollapse = () => {
        this.setState((prev) => ({ collapsed: !prev.collapsed }));
    };

    render() {
        const { tabs = [], children, contentRef } = this.props;
        const { activeTab, collapsed } = this.state;

        const activeTabData = tabs.find((t) => t.id === activeTab);

        return (
            <div className="tabbed-sidebar-layout">

                {/* ── Far-left tab rail ── */}
                <div className="tab-rail" role="tablist" aria-label="Sidebar tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={`tab-rail-btn${activeTab === tab.id ? ' active' : ''}`}
                            role="tab"
                            aria-selected={activeTab === tab.id}
                            aria-label={tab.label || `Tab ${tab.id}`}
                            onClick={() => this.selectTab(tab.id)}
                            title={tab.label}
                        >
                            {tab.icon}
                        </button>
                    ))}

                    <div className="tab-rail-spacer" />

                    {/* Show/hide toggle anchored to the bottom of the rail */}
                    <button
                        className="tab-rail-toggle"
                        onClick={this.toggleCollapse}
                        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {collapsed ? '›' : '‹'}
                    </button>
                </div>

                {/* ── Sidebar panel ── */}
                <div
                    className={`sidebar-panel${collapsed ? ' collapsed' : ''}`}
                    role="tabpanel"
                    aria-label={activeTabData ? activeTabData.label : 'Sidebar panel'}
                >
                    <div className="sidebar-panel-inner">
                        {activeTabData ? activeTabData.content : null}
                    </div>
                </div>

                {/* ── Main content area ── */}
                <div className="sidebar-content" ref={contentRef}>
                    {children}
                </div>

            </div>
        );
    }
}

export default TabbedSidebar;
